import { Box, Dialog, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";

import * as yup from "yup";
import { Field, Form, Formik } from "formik";
import { number, string } from "yup";
import dayjs, { Dayjs } from "dayjs";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AddJobCommand, FileData, JobSummary, Product, ProductSummary } from "../../../../api/models/schema";
import { StandardTextBox } from "../../../../common/standardTextBox";
import { StandardCheckBox } from "../../../../common/standardCheckBox";
import { StandardDropDown } from "../../../../common/standardDropDown";
import { UploadFileButton } from "../../../../common/uploadFileButton";
import { DropDownItem } from "../../../../common/dropDownItemViewModel";
import { BasicErrorLabel } from "../../../../common/basicErrorLabel";

export interface FormValues {
  jobNumber: string;
  jobName: string;
  isNCOA: boolean;
  ncoaDate?: Date;
  isSCF: boolean;
  mailDate: Date;
  preSortClass: string;
  productId: string;
  dropshipping: boolean;
}
const today = new Date();
today.setHours(0, 0, 0, 0);

const validationSchema = yup.object({
  jobNumber: string().min(2, "too short").max(15).required("required"),
  jobName: string().min(2, "too short").max(50).required("required"),
  mailDate: yup.date().required("required"),
  isNCOA: yup.boolean(),
  ncoaDate: yup.date().when("isNCOA", ([isNCOA], schema) => {
    return isNCOA
      ? schema.required("required").max(today, "Date must be in the past")
      : schema.notRequired();
  }),
  // isSCF: yup.boolean(),
  preSortClass: string().min(2, "too short").required("required"),
  productId: string().min(1, "Please select a product").required("required"),
});

export interface EditJobFormProps {
  job?: JobSummary;
  isNonProfit?: boolean;
  products: Product[];
  isLoading: boolean;
  open: boolean;
  onClose: () => void;
  onUpdate: (job: AddJobCommand) => void;
  errorMessage?: string;
}

export function EditJobForm(props: EditJobFormProps) {
  const [uploadFile, setUploadFile] = useState<FileData | undefined>(undefined);
  const [mailingDate, setMailingDate] = useState<Dayjs>(dayjs());
  const [ncoaDate, setNcoaDate] = useState<Dayjs | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [productList, setProductList] = useState<DropDownItem[] | undefined>(undefined);

  const preSortClassDropDownItem = [
    { id: "STANDARD MAIL", name: "STANDARD MAIL" },
    { id: "FIRST CLASS", name: "FIRST CLASS" }
  ];

  if(props.isNonProfit) {
    preSortClassDropDownItem.push({ id: "NON PROFIT", name: "NON PROFIT" });
  }

  useEffect(() => {
    if (props.products) {
      const _productList: DropDownItem[] = props.products.map((p) => {
        return { id: p.productId.toString(), name: p.name };
      });
      setProductList(_productList);
    }
  }, [props.products]);

  useEffect(() => {
    if (props.errorMessage) {
      setErrorMessage(props.errorMessage);
      setIsLoading(false);
    }
  }, [props.errorMessage]);

  useEffect(() => {
    if (props.open) {
      setIsLoading(false);
      setSelectedProduct(undefined);
      setUploadFile(undefined);
      setErrorMessage(undefined);
    }
  }, [props.open]);

  function update(values: FormValues) {
    setErrorMessage(undefined);
    if (uploadFile) {
      const updateJob: AddJobCommand = {
        jobNumber: values.jobNumber,
        jobName: values.jobName,
        isNCOA: values.isNCOA,
        ncoaDate: values.ncoaDate,
        isSCF: values.isSCF,
        mailDate: mailingDate.toDate(),
        dataFile: uploadFile,
        preSortClass: values.preSortClass,
        productId: Number(values.productId),
        dropshipping: values.dropshipping,
      };

      if (values.isNCOA && !values.ncoaDate) {
        setErrorMessage("Ncoa Date is required");
        return;
      }

      setIsLoading(true);
      props.onUpdate(updateJob);
    } else {
      alert("No file has been uploaded");
    }
  }

  function handleClose() {
    setIsLoading(false);
    props.onClose();
  }

  async function handleUploadDataFile(
    selectedFile: string,
    fileName: string,
    mimeType: string,
    size: number
  ) {
    if (selectedFile) {
      const fileInfo = selectedFile.split(",");
      let base64StringFile = selectedFile; 
      
      
      const fileData: FileData = {
        name: fileName,
        mimeType: mimeType,
        data: base64StringFile,
      };

      setUploadFile(fileData);
    }
  }

  function updateMailingDate(newDate: Dayjs | null) {
    if (newDate) {
      setMailingDate(newDate);
    }
  }
  function updateSelectedProduct(productId: string | null) {
    if (productId) {
      const id = Number(productId);
      const _product = props.products.find((p) => p.productId === id);
      setSelectedProduct(_product);
    }
  }

  function updateNcoaDate(newDate: Dayjs | null) {
    setNcoaDate(newDate);
  }

  function displayForm() {
    return (
      <Box>
        <Dialog open={props.open}   >
          <DialogTitle>Add Job</DialogTitle>
          <DialogContent>
            <Formik
              initialValues={{
                jobNumber: "",
                jobName: "",
                mailDate: new Date(),
                isNCOA: false,
                isSCF: false,
                ncoaDate: undefined,
                preSortClass: "STANDARD MAIL",
                productId: '',
                dropshipping: true,
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                setSubmitting(false);
                update(values);
              }}
            >
              {({
                submitForm,
                isSubmitting,
                setFieldValue,
                touched,
                errors,
              }) => (
                <Form>
                  <br />

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      Job Number
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="jobNumber"
                        label="Job Number"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Job Name
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="jobName"
                        label="Job Name"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Job Date
                    </Grid>
                    <Grid item xs={9}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Mailing Date"
                          value={mailingDate}
                          onChange={(newDate) => updateMailingDate(newDate)}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={3}>
                      IsNCOA
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        type="checkbox"
                        component={StandardCheckBox}
                        tooltip="select this if the data has already been NCOA processed"
                        name="isNCOA"
                        label="Already NCOA Processed"
                        width={300}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Dropshipping
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        type="checkbox"
                        component={StandardCheckBox}
                        tooltip="uncheck if this job should not be dropshipped"
                        name="dropshipping"
                        label="Dropshipping"
                        width={300}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      NCOA Date
                    </Grid>
                    <Grid item xs={9}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="NCOA Date"
                          name="ncoaDate"
                          value={ncoaDate}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              error:
                                touched.ncoaDate && Boolean(errors.ncoaDate),
                              helperText: touched.ncoaDate && errors.ncoaDate,
                            },
                          }}
                          onChange={(newDate) =>
                            setFieldValue("ncoaDate", newDate, true)
                          }
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={3}>
                      Upload Data
                    </Grid>
                    <Grid item xs={9}>
                      { (
                        <Box>
                          <UploadFileButton
                            id="uploadDataFile"
                            accept=".csv"
                            onChange={handleUploadDataFile}
                          />
                         {!uploadFile && (<p>accepts csv file</p>)}
                        </Box>
                      )}
                      {uploadFile && (
                        <Box sx={{ fontWeight: 700, marginTop: '5px' }}>
                          {uploadFile.name?.substring(0, 30)}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={3}>
                      PreSort Class
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardDropDown}
                        name="preSortClass"
                        label="PreSort Class"
                        items={preSortClassDropDownItem}
                        width={375}
                        onChange={(e: any) => {
                          console.log("preSortClass id:>> ", e.target.value);
                          setFieldValue("preSortClass", e.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Product
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardDropDown}
                        name="productId"
                        label="Product"
                        items={productList}
                        width={375}
                        value={selectedProduct?.productId}
                        onChange={(e: any) => {
                          setFieldValue("productId", e.target.value);
                          updateSelectedProduct(e.target.value);
                        }}
                      />
                    </Grid>
                    {selectedProduct && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          justifyContent: "end",
                          fontSize: "13px",
                        }}
                      >
                        <table>
                          <tr>
                            <td style={{ width: 100 }}>Height</td>
                            <td style={{ width: 100 }}>
                              <b>{selectedProduct?.height}</b>
                            </td>
                            <td style={{ width: 100 }}>Thickness</td>
                            <td style={{ width: 100 }}>
                              <b>{selectedProduct?.thickness}</b>
                            </td>
                          </tr>
                          <tr>
                            <td>Length</td>
                            <td>
                              <b>{selectedProduct?.length}</b>
                            </td>
                            <td>Min 2 Ft</td>
                            <td>
                              <b>{selectedProduct?.min2Ft}</b>
                            </td>
                          </tr>
                          <tr>
                            <td>Weight</td>
                            <td>
                              <b>{selectedProduct?.weight}</b>
                            </td>
                            <td>Max 2 Ft</td>
                            <td>
                              <b>{selectedProduct?.max2Ft}</b>
                            </td>
                          </tr>
                          <tr>
                            <td>Tray Type</td>
                            <td>
                              <b>{selectedProduct?.trayType}</b>
                            </td>
                            <td>Mail Piece Size</td>
                            <td>
                              <b>{selectedProduct?.mailPieceSize}</b>
                            </td>
                          </tr>
                          <tr>
                              <td>Pallet Min Trays</td>
                              <td>
                                  <b>{selectedProduct?.palletMinTrays}</b>
                              </td>
                              <td>Pallet Max Trays</td>
                              <td>
                                  <b>{selectedProduct?.palletMaxTrays}</b>
                              </td>
                           </tr>
                           <tr>
                               <td>Postage Affixed</td>
                               <td>
                                   <b>{selectedProduct?.postageAffixed}</b>
                               </td>
                               <td>North South</td>
                               <td>
                                   <b>{selectedProduct?.northSouth}</b>
                               </td>
                           </tr>
                        </table>
                      </Grid>
                    )}

                    <Grid
                      sx={{ marginTop: "10px" }}
                      spacing={2}
                      item
                      container
                      xs={12}
                      justifyContent="flex-end"
                    >
                      {!isLoading && (
                        <Grid item>
                          <Button variant="contained" onClick={submitForm}>
                            Create
                          </Button>
                        </Grid>
                      )}
                      {isLoading && (
                        <Grid item>
                          <CircularProgress />
                        </Grid>
                      )}
                      {!isLoading && (
                        <Grid item>
                        <Button onClick={handleClose}>Cancel</Button>
                      </Grid>
                      )}
                      {isLoading && errorMessage === undefined && (
                        <Grid item>
                        <Button onClick={handleClose}>Done</Button>
                      </Grid>
                      )}
                      
                    </Grid>

                    <Grid item xs={12}>
                      {errorMessage && (
                        <BasicErrorLabel message={errorMessage} />
                      )}
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  return <>{displayForm()}</>;
}
